/**
 * product-grid.js
 * Custom element <product-grid> con infinite scroll para Shopify.
 * Subir a: Online Store → Themes → Edit code → Assets
 */

class ProductGrid extends HTMLElement {
  constructor() {
    super();
    this._isLoading   = false;
    this._observer    = null;
    this._resizeTimer = null;
    this._onResize    = this._onResize.bind(this);
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  connectedCallback() {
    this._currentPage = parseInt(this.getAttribute('data-page') || '1');
    this._collection  = this.getAttribute('data-collection');
    this._layout      = this.getAttribute('data-layout') || 'grid';

    this._grid       = this.querySelector('#product-gird-loop');
    this._pagination = this.querySelector('#infinite-Pagination');

    if (!this._grid || !this._pagination) return;

    this._injectLoaderStyles();
    this._injectLoader();   // loader como hermano de _pagination, no dentro

    this._syncBannerHeights();
    window.addEventListener('resize', this._onResize);

    this._initObserver();

    if (this.getAttribute('data-opacity-filter') === 'true') {
      const initialColumns = Array.from(this._grid.querySelectorAll('.product-card__column'));
      this._filterByUrlParams(initialColumns);
    }

    // Restore collapse state for freshly-rendered cards (e.g. after AJAX filter update)
    this._handleCollapse();
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
    window.removeEventListener('resize', this._onResize);
  }

  // ─── Loader ───────────────────────────────────────────────────────────────

  _injectLoaderStyles() {
    if (document.getElementById('product-grid-loader-styles')) return;
    const style = document.createElement('style');
    style.id = 'product-grid-loader-styles';
    style.textContent = `
      .pg-loader {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        padding: 2.5rem 0;
        opacity: 0;
        transition: opacity 0.2s ease;
        pointer-events: none;
      }
      .pg-loader--visible {
        opacity: 1;
      }
      .pg-loader__dots {
        display: flex;
        gap: 8px;
        align-items: center;
      }
      .pg-loader__dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: currentColor;
        opacity: 0.3;
        animation: pg-bounce 1.2s ease-in-out infinite;
      }
      .pg-loader__dot:nth-child(1) { animation-delay: 0s; }
      .pg-loader__dot:nth-child(2) { animation-delay: 0.2s; }
      .pg-loader__dot:nth-child(3) { animation-delay: 0.4s; }
      .pg-loader__dot:nth-child(4) { animation-delay: 0.6s; }
      .pg-loader__dot:nth-child(5) { animation-delay: 0.8s; }
      @keyframes pg-bounce {
        0%, 80%, 100% { transform: translateY(0);     opacity: 0.3; }
        40%            { transform: translateY(-10px); opacity: 1;   }
      }
    `;
    document.head.appendChild(style);
  }

  _injectLoader() {
    // El loader va DESPUÉS de _pagination, no dentro.
    // Así el innerHTML de _pagination no lo destruye al actualizarse.
    if (this._loader) return;

    const loader = document.createElement('div');
    loader.className = 'pg-loader';
    loader.innerHTML = `
      <div class="pg-loader__dots">
        <span class="pg-loader__dot"></span>
        <span class="pg-loader__dot"></span>
        <span class="pg-loader__dot"></span>
        <span class="pg-loader__dot"></span>
        <span class="pg-loader__dot"></span>
      </div>
    `;

    // Insertar como hermano justo después de la paginación
    this._pagination.insertAdjacentElement('afterend', loader);
    this._loader = loader;
  }

  _setLoaderVisible(visible) {
    if (!this._loader) return;
    this._loader.classList.toggle('pg-loader--visible', visible);
  }

  // ─── Scroll infinito ──────────────────────────────────────────────────────

  _initObserver() {
    this._observer = new IntersectionObserver(
      (entries) => this._onIntersect(entries),
      { rootMargin: '400px' }
    );
    this._observer.observe(this._pagination);
  }

  async _onIntersect(entries) {
    if (!entries[0].isIntersecting || this._isLoading) return;

    const nextLink = this._pagination.querySelector('a');
    if (!nextLink) {
      this._observer.disconnect();
      return;
    }

    await this._loadNextPage(nextLink.href);
  }

  async _loadNextPage(url) {
    this._isLoading = true;
    this._setLoaderVisible(true);

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

      // Insertar nuevos productos
      const newProducts = doc.querySelectorAll('#product-gird-loop .product-card__column');
      newProducts.forEach((p) => this._grid.appendChild(p));

      // Insertar banners de colección
      const newBanners = doc.querySelectorAll('#product-gird-loop .collection-grid-banner');
      newBanners.forEach((b) => this._grid.appendChild(b));

      // Actualizar paginación (no afecta al loader porque está fuera)
      const newPagination = doc.querySelector('#infinite-Pagination');
      this._pagination.innerHTML = newPagination ? newPagination.innerHTML : '';

      // Actualizar página
      this._currentPage++;
      this.setAttribute('data-page', this._currentPage);

      // Post-carga
      this._handleCollapse();
      this._updateBannerVisibility();
      this._syncBannerHeights();
      this._initNewCards(newProducts);
      this._filterByUrlParams(newProducts);

      this.dispatchEvent(new CustomEvent('product-grid:loaded', {
        bubbles: true,
        detail: { page: this._currentPage, count: newProducts.length },
      }));

      if (!this._pagination.querySelector('a')) {
        this._observer.disconnect();
      }

    } catch (err) {
      console.error('[product-grid] Error cargando página:', err);
    } finally {
      this._isLoading = false;
      this._setLoaderVisible(false);
    }
  }

  // ─── Collapse (layout 1 columna en móvil) ────────────────────────────────

  _handleCollapse() {
    const isOneCol = document
      .querySelector('.product-grid-selector')
      ?.classList.contains('grid-cols--1');

    if (!isOneCol || window.innerWidth >= 991) return;

    document
      .querySelectorAll('.collapse.product-card__stack')
      .forEach((el) => el.classList.add('collapse--active'));

    document
      .querySelectorAll('.collapse__body.product-card__stack__body')
      .forEach((el) => (el.style.display = 'block'));
  }

  // ─── Visibilidad de banners según página ─────────────────────────────────

  _updateBannerVisibility() {
    const isMobile    = window.innerWidth < 992;
    const rowAttr     = isMobile ? 'data-mobile-row' : 'data-row';
    const rowsPerPage = isMobile ? 4 : 2;
    const maxRow      = this._currentPage * rowsPerPage;

    document.querySelectorAll('.collection-grid-banner').forEach((banner) => {
      const row = parseInt(banner.getAttribute(rowAttr) || '0', 10);
      banner.classList.toggle('hide', row > maxRow);
    });
  }

  // ─── Altura de banners = altura de tarjeta ────────────────────────────────

  _syncBannerHeights() {
    const card    = this._grid.querySelector('.product-card');
    const banners = document.querySelectorAll('.collection-grid-banner');

    if (!card || banners.length === 0) return;

    const offset = window.innerWidth < 991 ? 0 : 3;
    const height = card.offsetHeight - offset;
    banners.forEach((b) => (b.style.height = `${height}px`));
  }

  _onResize() {
    clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => {
      this._syncBannerHeights();
      this._updateBannerVisibility();
    }, 100);
  }

  // ─── Inicializar nuevas ProductCards ─────────────────────────────────────

  _initNewCards(columns) {
    columns.forEach((col) => {
      const card = col.querySelector('.product-card');
      if (card && typeof ProductCard !== 'undefined') new ProductCard(card);
    });
  }

  // ─── Filtrar columnas sin swatches cuando hay filtros en la URL ──────────

  _filterByUrlParams(columns) {
    if (!window.location.search) return;

    const SWATCH_THRESHOLD = 6;

    setTimeout(() => {
      columns.forEach((col) => {
        const items = col.querySelectorAll('.baslide__item');
        const swatchCount = Array.from(items).filter(
          (item) => item.querySelector('.product-card__swatch')
        ).length;

        if (swatchCount < 1) {
          col.remove();
          return;
        }
        if (swatchCount < SWATCH_THRESHOLD) {
          col.classList.add('hide-arrows');
        }
      });

      setTimeout(() => {
        columns.forEach((col) => {
          if (col.isConnected) col.style.opacity = '1';
        });
      }, 100);

    }, 400);
  }
}

customElements.define('product-grid', ProductGrid);